import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Editor from '@monaco-editor/react';
import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Sparkles, Pencil, Trash2, Loader2, X, Info, Download } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select';
import { useToast } from '@/shared/components/ui/use-toast';
import { useTheme } from '@/shared/hooks/use-theme';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/shared/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog';
import { WorkspaceOverviewBlock, BlockType } from '../types/WorkspaceOverviewTypes';
import { WorkspaceOverviewService } from '../services/workspaceOverviewService';
import { formatContentAsMarkdown } from '../utils/blockContentFormatter';
import { parseMarkdownToBlockContent } from '../utils/blockMarkdownParser';
import { GroupContent } from './GroupContent';
import { FacetSelector } from '@/features/Facets/components/FacetSelector';
import type { FacetFilter } from '@/features/Facets/types';

interface Props {
  block: WorkspaceOverviewBlock;
  workspaceId: string;
  initialEditing?: boolean;
  onDelete: (blockId: string) => void;
  onBlockUpdate: (blockId: string, updates: Partial<WorkspaceOverviewBlock>) => void;
}

export const OverviewMarkdownEditor = ({
  block,
  workspaceId,
  initialEditing = false,
  onDelete,
  onBlockUpdate,
}: Props) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { theme } = useTheme();

  const [markdown, setMarkdown] = useState('');

  const [isRecreating, setIsRecreating] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showRecreateDialog, setShowRecreateDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(initialEditing);

  // Editable metadata
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editBlockType, setEditBlockType] = useState<BlockType>('text');
  const [editFieldName, setEditFieldName] = useState('');
  // PBI 79319 (Camada 2 — Fase 7.5) — facetas atribuídas ao bloco.
  // Persistidas dentro de block.content.facets (jsonb extensível) para evitar
  // alteração de schema. Quando o bloco vira embedding, o pipeline pode propagar.
  const [editFacets, setEditFacets] = useState<FacetFilter[]>([]);
  const [showEditorHint, setShowEditorHint] = useState(false);
  const [isEditorHintFading, setIsEditorHintFading] = useState(false);
  const hasShownEditorHint = useRef((() => { try { return localStorage.getItem('overview-editor-hint-shown') === 'true'; } catch { return false; } })());

  const isReadOnly = block.block_type === 'group';

  const BLOCK_TYPE_OPTIONS = useMemo(() => [
    { value: 'text' as BlockType, label: t('knowledge.overview.block_types.text.label') },
    { value: 'textarea' as BlockType, label: t('knowledge.overview.block_types.textarea.label') },
    { value: 'rich_text' as BlockType, label: t('knowledge.overview.block_types.rich_text.label') },
    { value: 'link' as BlockType, label: t('knowledge.overview.block_types.link.label') },
    { value: 'links' as BlockType, label: t('knowledge.overview.block_types.links.label') },
    { value: 'list' as BlockType, label: t('knowledge.overview.block_types.list.label') },
    { value: 'checklist' as BlockType, label: t('knowledge.overview.block_types.checklist.label') },
    { value: 'code' as BlockType, label: t('knowledge.overview.block_types.code.label') },
    { value: 'quote' as BlockType, label: t('knowledge.overview.block_types.quote.label') },
    { value: 'callout' as BlockType, label: t('knowledge.overview.block_types.callout.label') },
    { value: 'group' as BlockType, label: t('knowledge.overview.block_types.group.label') },
  ], [t]);

  useEffect(() => {
    setMarkdown(formatContentAsMarkdown(block));
    // Intentional: only reset markdown when the block identity changes, not on every field edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [block.id]);

  useEffect(() => {
    setEditTitle(block.title);
    setEditDescription(block.description || '');
    setEditBlockType(block.block_type);
    setEditFieldName(block.field_name);
    const rawFacets = (block.content as { facets?: unknown })?.facets;
    setEditFacets(Array.isArray(rawFacets) ? (rawFacets as FacetFilter[]) : []);
  }, [block.id, block.title, block.description, block.block_type, block.field_name, block.content]);

  useEffect(() => {
    setIsEditing(initialEditing);
  }, [block.id, initialEditing]);

  // Auto-close editor hint
  useEffect(() => {
    if (showEditorHint) {
      const timer = setTimeout(() => {
        setIsEditorHintFading(true);
        setTimeout(() => {
          setShowEditorHint(false);
          setIsEditorHintFading(false);
        }, 500);
      }, 7000);
      return () => clearTimeout(timer);
    }
  }, [showEditorHint]);

  const handleChange = (value: string | undefined) => {
    const val = value ?? '';
    setMarkdown(val);
    if (!isReadOnly) {
      const content = parseMarkdownToBlockContent(val, block.block_type);
      onBlockUpdate(block.id, { content });
    }
  };

  const handleRecreate = async () => {
    setIsRecreating(true);
    try {
      const result = await WorkspaceOverviewService.generateBlockContent(
        workspaceId,
        block.block_type,
        block.title,
        block.description
      );
      onBlockUpdate(block.id, { content: result.content });
      const newMarkdown = formatContentAsMarkdown({ ...block, content: result.content });
      setMarkdown(newMarkdown);
        toast({ title: t('common.success'), description: t('knowledge.overview.content_recreated') });
    } catch {
      toast({
        title: t('common.error'),
        description: t('knowledge.overview.error_recreating'),
        variant: 'destructive',
      });
    } finally {
      setIsRecreating(false);
    }
  };


  const handleDownload = useCallback(() => {
    const filename = `${block.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.md`;
    const blob = new Blob([markdown], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 100);
  }, [block.title, markdown]);

  const handleToggleEdit = () => {
    if (isEditing) {
      setEditTitle(block.title);
      setEditDescription(block.description || '');
      setEditBlockType(block.block_type);
      setShowEditorHint(false);
    } else if (!hasShownEditorHint.current) {
      hasShownEditorHint.current = true;
      try { localStorage.setItem('overview-editor-hint-shown', 'true'); } catch { /* storage blocked */ }
      setTimeout(() => setShowEditorHint(true), 600);
    }
    setIsEditing(!isEditing);
  };

  const markdownComponents: Components = {
    pre: ({ className, children, ...props }) => (
      <pre
        className={`overflow-auto max-h-64 rounded-md bg-muted p-3 text-xs font-mono whitespace-pre-wrap break-all text-foreground ${className || ''}`}
        {...props}
      >
        {children}
      </pre>
    ),
    code: ({ className, children, ...props }: React.ComponentPropsWithoutRef<'code'> & { inline?: boolean }) => {
      const { inline } = props as { inline?: boolean };
      if (inline) {
        return (
          <code className={`font-mono bg-muted text-foreground px-1 py-0.5 rounded text-xs break-words whitespace-pre-wrap ${className || ''}`} {...props}>
            {children}
          </code>
        );
      }
      return (
        <code className={`font-mono block whitespace-pre-wrap break-all text-foreground ${className || ''}`} {...props}>
          {children}
        </code>
      );
    },
    a: ({ href, children, ...props }) => (
      <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
        {children}
      </a>
    ),
    table: ({ children, ...props }) => (
      <div className="overflow-x-auto my-4">
        <table className="w-full border-collapse text-sm" {...props}>{children}</table>
      </div>
    ),
    thead: ({ children, ...props }) => (
      <thead className="bg-muted/50" {...props}>{children}</thead>
    ),
    th: ({ children, ...props }) => (
      <th className="border border-border px-3 py-2 text-left font-semibold text-foreground" {...props}>{children}</th>
    ),
    td: ({ children, ...props }) => (
      <td className="border border-border px-3 py-2 text-foreground" {...props}>{children}</td>
    ),
    tr: ({ children, ...props }) => (
      <tr className="even:bg-muted/20" {...props}>{children}</tr>
    ),
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 px-4 py-2 border-b shrink-0 bg-muted/30">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-[11px] font-mono tracking-wide text-muted-foreground/70 uppercase truncate">{t(`knowledge.overview.section_titles.${block.field_name.toLowerCase().replace(/-/g, '_')}`, block.field_name)}</span>
          <span className="text-muted-foreground/30 text-xs">·</span>
          <Badge variant="outline" className="text-[10px] shrink-0 text-muted-foreground">
            {BLOCK_TYPE_OPTIONS.find(opt => opt.value === block.block_type)?.label ?? block.block_type}
          </Badge>
          {isReadOnly && (
            <Badge variant="secondary" className="text-[10px] shrink-0">{t('knowledge.overview.read_only')}</Badge>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={handleDownload}
          >
            <Download className="w-3.5 h-3.5" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setShowDeleteDialog(true)} className="h-8 w-8 p-0">
            <Trash2 className="w-3.5 h-3.5" />
          </Button>

          {!isEditing && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleToggleEdit}
                    className="h-8 w-8 p-0"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs">{t('knowledge.overview.edit_mode')}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          {isEditing && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleToggleEdit}
                    className="h-8 w-8 p-0"
                  >
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs">{t('knowledge.overview.view_mode')}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

        </div>
      </div>

      {/* Two-column content */}
      <div className="flex-1 overflow-hidden flex">
        {/* Left: Editor column (wiki-style) */}
        <div
          className={`overflow-hidden border-r transition-all duration-300 ease-in-out ${
            isEditing ? 'flex-1 opacity-100' : 'w-0 flex-none opacity-0'
          }`}
        >
          <div className="h-full flex flex-col min-w-0">
            {/* Inline title + metadata */}
            <div className="px-5 pt-4 pb-3 space-y-2.5 shrink-0 border-b border-dashed">
              {/* Title row with AI button */}
              <div className="flex items-start justify-between gap-3">
                <input
                  value={editTitle}
                  onChange={(e) => { setEditTitle(e.target.value); onBlockUpdate(block.id, { title: e.target.value }); }}
                  placeholder={t('knowledge.overview.block_title_placeholder')}
                  className="flex-1 text-xl font-bold bg-transparent border-none outline-none placeholder:text-muted-foreground/40 text-foreground"
                />
                <div className="relative shrink-0">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => { setShowRecreateDialog(true); setShowEditorHint(false); }}
                          disabled={isRecreating}
                          className="h-8 w-8 border-[#0B6FC2]/30 text-[#0B6FC2] hover:bg-[#0B6FC2]/10"
                        >
                          {isRecreating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent><p className="text-xs">{t('knowledge.overview.recreate_content')}</p></TooltipContent>
                    </Tooltip>
                  </TooltipProvider>

                  {showEditorHint && (
                    <div
                      className="absolute top-11 right-0 z-50"
                      style={{
                        animation: isEditorHintFading
                          ? 'editorHintExit 500ms ease-out forwards'
                          : 'editorHintEnter 1s cubic-bezier(0.34, 1.56, 0.64, 1) forwards',
                      }}
                    >
                      <style>{`
                        @keyframes editorHintEnter {
                          0% { opacity: 0; transform: translateY(8px) scale(0.96); }
                          100% { opacity: 1; transform: translateY(0) scale(1); }
                        }
                        @keyframes editorHintExit {
                          0% { opacity: 1; transform: translateY(0) scale(1); }
                          100% { opacity: 0; transform: translateY(-4px) scale(0.98); }
                        }
                      `}</style>
                      <div className="absolute -top-2 right-[12px] w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-b-[8px] border-b-border" />
                      <div className="absolute -top-[7px] right-[12px] w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-b-[8px] border-b-background" />
                      <div className="bg-background border rounded-lg p-3 shadow-lg w-[200px] relative">
                        <button onClick={() => setShowEditorHint(false)} className="absolute top-1.5 right-1.5 text-muted-foreground hover:text-foreground">
                          <X className="w-3 h-3" />
                        </button>
                        <div className="flex items-start gap-2">
                          <Info className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
                          <p className="text-xs text-foreground leading-relaxed">
                            {t('knowledge.overview.editor_ai_hint')}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {/* Headline / description — subtle editable line */}
              <input
                value={editDescription}
                onChange={(e) => { setEditDescription(e.target.value); onBlockUpdate(block.id, { description: e.target.value }); }}
                placeholder={t('knowledge.overview.description_placeholder')}
                className="w-full text-sm bg-transparent border-none outline-none text-muted-foreground placeholder:text-muted-foreground/30"
              />
              {/* Metadata row — field_name + block_type inline */}
              <div className="flex items-center gap-2 pt-1">
                <input
                  value={editFieldName}
                  onChange={(e) => {
                    const val = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_');
                    setEditFieldName(val);
                    onBlockUpdate(block.id, { field_name: val });
                  }}
                  className="font-mono text-[10px] text-muted-foreground bg-transparent border border-dashed border-muted-foreground/30 rounded px-1.5 py-0.5 outline-none focus:border-primary focus:text-foreground w-32 min-w-0"
                  spellCheck={false}
                />
                <span className="text-muted-foreground/30">·</span>
                <Select
                  value={editBlockType}
                  onValueChange={(value) => { setEditBlockType(value as BlockType); onBlockUpdate(block.id, { block_type: value as BlockType }); }}
                >
                  <SelectTrigger className="h-6 w-auto border-none shadow-none px-1.5 text-[11px] text-muted-foreground hover:bg-muted/60 gap-1">
                    <SelectValue>
                      {BLOCK_TYPE_OPTIONS.find(opt => opt.value === editBlockType)?.label}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {BLOCK_TYPE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value} className="text-xs">
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {/* PBI 79319 — Facetas (Camada 2). Armazenadas em content.facets para evitar schema change. */}
              <div className="pt-1">
                <FacetSelector
                  value={editFacets}
                  onChange={(next) => {
                    setEditFacets(next);
                    onBlockUpdate(block.id, {
                      content: { ...block.content, facets: next },
                    });
                  }}
                />
              </div>
            </div>

            {/* Monaco editor — takes all remaining space */}
            <div className="flex-1 overflow-hidden">
              <Editor
                height="100%"
                language="markdown"
                value={markdown}
                onChange={handleChange}
                theme={theme === 'dark' ? 'vs-dark' : 'vs'}
                options={{
                  minimap: { enabled: false },
                  wordWrap: 'on',
                  fontSize: 13,
                  lineHeight: 20,
                  readOnly: isReadOnly,
                  scrollBeyondLastLine: false,
                  padding: { top: 12 },
                  lineNumbers: 'off',
                  glyphMargin: false,
                  folding: false,
                  renderLineHighlight: 'none',
                  overviewRulerLanes: 0,
                  hideCursorInOverviewRuler: true,
                  scrollbar: { vertical: 'auto', horizontal: 'hidden' },
                }}
              />
            </div>
          </div>
        </div>

        {/* Right: Rendered preview — always visible */}
        <div className="flex-1 overflow-y-auto">
          <div className="px-8 py-6 max-w-3xl">
            {/* Preview title */}
            <h1 className="text-xl font-bold mb-1">{isEditing ? editTitle || block.title : block.title}</h1>
            {(isEditing ? editDescription : block.description) && (
              <p className="text-sm text-muted-foreground mb-4">
                {isEditing ? editDescription : block.description}
              </p>
            )}
            <div className="prose prose-sm max-w-none dark:prose-invert">
              {isReadOnly ? (
                <GroupContent items={block.content.personas} />
              ) : (
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{markdown}</ReactMarkdown>
              )}
            </div>
          </div>
        </div>
      </div>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('knowledge.overview.delete_confirm_title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('knowledge.overview.delete_confirm_description')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setShowDeleteDialog(false); onDelete(block.id); }}>
              {t('knowledge.overview.delete_confirm_action')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showRecreateDialog} onOpenChange={setShowRecreateDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('knowledge.overview.recreate_confirm_title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('knowledge.overview.recreate_confirm_description')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setShowRecreateDialog(false); handleRecreate(); }}>
              {t('knowledge.overview.recreate_confirm_action')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
